import { useMutation } from '@apollo/react-hooks';
import { Grid, Box } from '@material-ui/core';
import {
	Button,
	TextBlock,
	TextField
} from 'neon-design-system';
import React, { ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { CREATE_INVOICE_PAYMENT_REQUEST } from '../../../gql/subscriptions';
import { Channel, InvoicePaymentRequestInput } from '../../../shared/types';
import TermsAndConditionsCheckbox from '../../ui/TermsAndConditionsCheckbox';
import { useFormControl, useFormGroup } from '../../../utils/form.utils';
import { booleanTrueValidator, requiredValidator, validateEmail } from '../../../utils/validator.utils';

interface PremiumChannelInvoicePaymentProps {
	channelId: string;
	successUrl: string;
	goBack: () => void;
}

export const PremiumChannelInvoicePayment = (props: PremiumChannelInvoicePaymentProps) => {
	const { channelId, successUrl, goBack } = props;

	const form = useFormGroup({
		legalEntityName: useFormControl<string>('', [requiredValidator]),
		billingEmailAddress: useFormControl<string>('', [requiredValidator, validateEmail]),
		acceptedTerms: useFormControl<boolean>(false, [booleanTrueValidator])
	});

	const history = useHistory();

	const [createInvoiceChannelSubscription] =
		useMutation<Channel, { item: InvoicePaymentRequestInput }>(CREATE_INVOICE_PAYMENT_REQUEST, {
			onCompleted: () => {
				history.replace(successUrl);
			}
		});

	const onSubmit = async (ev: any) => {
		if (!channelId) return;

		ev?.preventDefault();
		form.setDirty();
		if (!form.isValid) {
			return;
		}

		return createInvoiceChannelSubscription({
			variables: {
				item: {
					channelId,
					legalEntityName: form.value.legalEntityName,
					billingEmailAddress: form.value.billingEmailAddress
				}
			}
		});
	}

	return (
		<Grid container spacing={4}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextBlock styleName="body">
							A member of the Neon Concierge team will get in touch.
						</TextBlock>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id={'legalEntityName-input'}
							fullWidth
							size="large"
							error={form.legalEntityName.showError}
							errorText={form.legalEntityName.validationMessage}
							type="text"
							label="Legal entity name"
							autoComplete="off"
							value={form.legalEntityName.value}
							onBlur={form.legalEntityName?.handleBlur}
							onChange={(e: ChangeEvent<any>): void => {
								const val: string = e?.target?.value;
								form.legalEntityName?.setValue(val);
							}}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id={'billingEmailAddress-input'}
							fullWidth
							size="large"
							error={form.billingEmailAddress.showError}
							errorText={form.billingEmailAddress.validationMessage}
							type="text"
							label="Billing email address"
							autoComplete="off"
							value={form.billingEmailAddress.value}
							onBlur={form.billingEmailAddress.handleBlur}
							onChange={(e: ChangeEvent<any>): void => {
								const val: string = e?.target?.value;
								form.billingEmailAddress.setValue(val);
							}}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12}>
				<TermsAndConditionsCheckbox
					acceptedTerms={form.acceptedTerms.value}
					setAcceptedTerms={(acceptedTerms: boolean) => form.acceptedTerms.setValue(acceptedTerms)}
					showError={form.acceptedTerms.showError}
				/>
			</Grid>
			<Grid item xs={12}>
				<Box display="flex">
					<Box mr={1}>
						<Button
							colorType="grey"
							onClick={goBack}
						>
							Back
						</Button>
					</Box>
					<Box>
						<Button
							colorType="primary"
							onClick={onSubmit}
						>
							Send invoice request
						</Button>
					</Box>
				</Box>
			</Grid>
		</Grid>
	);
}
